// What a stay costs — ONE definition, read by the desk and (from
// `availability-and-request` onward) by the customer portal.
//
// MOVED HERE by portal-foundations AC8. It lived in
// apps/web/src/components/bookings/domain.ts, which is a browser module inside
// the admin SPA: the portal cannot import it, and the alternative to moving it
// was the portal quoting a price computed by a second copy of these four
// numbers. A household told €130 by the app and €125 by Patricia is the kind of
// bug that costs trust rather than time.
//
// THE DESK'S PRICES DO NOT CHANGE. The rules and the constants below are
// carried across unaltered, and the web module now re-exports these rather than
// declaring its own — so apps/web/tests/unit/booking-pricing.test.ts and the
// calcBookingTotal block in format.test.ts run, unedited, against this file.
// That is what AC8 asks to be proven, and it is proven by those suites staying
// green rather than by new assertions.

// Per-night rate by kennel area (and, for a dog, by its size — the two share a
// vocabulary and a number). `mix` is priced as `big`: a mixed household is a
// big kennel with a little dog in it.
export const AREA_RATES: Record<string, number> = {
  big: 35,
  little: 30,
  mix: 35,
};

// The fallback when an area is unrecognised — a legacy `house` booking, or a
// blank on a half-filled form. `big` is the desk's own default area.
export const DEFAULT_AREA_RATE = AREA_RATES.big!;

// Prototype data.js: a single-night stay is charged this flat rate instead of
// the per-area nightly one. A one-night stay is nearly all handover.
export const ONE_NIGHT_RATE = 40;

// Shared-kennel discount: two dogs in one kennel come off €5 cheaper than the
// sum of their rates (Freddi 2026-06-25). Exactly two — one dog has nobody to
// share with and three or more are not sharing one kennel.
export const SHARED_KENNEL_DISCOUNT = 5;

// One dog's nightly rate: its SIZE rate, or the booking's area rate when the
// size is unknown. The area the staff chose is the size proxy for a legacy or
// unsized dog — which is a real case, since a never-booked dog imports as
// 'unknown' (see DOG_SIZES_STORED in ./enums).
export function perDogNightly(size: string | null | undefined, areaRate: number): number {
  return AREA_RATES[size ?? ""] ?? areaRate;
}

// THE household nightly rate for a booking's dogs (Freddi 2026-06-25): the sum
// of each dog's per-size rate, minus €5 when EXACTLY two dogs share a kennel.
// One dog → its own rate; three or more → plain sum, no discount. No dogs → the
// area rate (a legacy guard: an imported booking can have none).
//
// Editable in the form; this is the computed default, not a price the desk is
// held to.
export function perNightForDogs(
  sizes: (string | null | undefined)[],
  areaRate: number,
): number {
  if (sizes.length === 0) return areaRate;
  const sum = sizes.reduce((acc, s) => acc + perDogNightly(s, areaRate), 0);
  return sum - (sizes.length === 2 ? SHARED_KENNEL_DISCOUNT : 0);
}

// Booking total — carried verbatim from _prototype/data.js calcBookingTotal.
// Single-night stays use the flat ONE_NIGHT_RATE; each extra dog adds 60% of
// the nightly rate.
//
// NOTE for anyone reaching for this from a new surface: it is the PROTOTYPE's
// pricing model (per-booking area rate × a dog multiplier), and the desk's
// modal has since moved to perNightForDogs (per-dog size rates). This one is
// still used for the create-form's opening estimate. Read
// `displayRatePerNight` in the web domain module before choosing between them.
export function calcBookingTotal(args: {
  area: string;
  checkIn: string;
  checkOut: string;
  dogs: number;
}): { nights: number; ratePerNight: number; total: number } {
  const { area, checkIn, checkOut, dogs } = args;
  if (!checkIn || !checkOut) return { nights: 0, ratePerNight: 0, total: 0 };
  const a = new Date(checkIn + "T00:00:00");
  const b = new Date(checkOut + "T00:00:00");
  const nights = Math.max(0, Math.round((b.getTime() - a.getTime()) / 86400000));
  const areaRate = AREA_RATES[area] ?? DEFAULT_AREA_RATE;
  const ratePerNight = nights === 1 ? ONE_NIGHT_RATE : areaRate;
  const dogCount = Math.max(1, dogs || 1);
  const total = nights * ratePerNight * (1 + (dogCount - 1) * 0.6);
  return { nights, ratePerNight, total: Math.round(total) };
}
